import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ArrowLeft } from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';

export default function TermsOfServicePage() {
  const { theme } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="terms-of-service-page" className={`min-h-screen pt-24 ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Back link */}
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 mb-8 hover:text-[#00C8EB] transition-colors ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-xl bg-[#464B8B]/10 flex items-center justify-center">
              <FileText size={28} weight="duotone" className="text-[#464B8B]" />
            </div>
            <div>
              <h1 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Terms of Service
              </h1>
              <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Last updated: March 2026
              </p>
            </div>
          </div>

          {/* Content */}
          <div className={`prose prose-lg max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`}>
            <div className={`space-y-8 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              
              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  1. Introduction and Acceptance
                </h2>
                <p>
                  These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and Atlas Softech Limited ("Company," "we," "our," or "us"), a company registered and operating under the laws of the United Republic of Tanzania.
                </p>
                <p>
                  By accessing or using our website, services, or products, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use our services.
                </p>
                <p>
                  These Terms are governed by and shall be construed in accordance with the laws of the United Republic of Tanzania, including but not limited to the <strong>Law of Contract Act</strong>, the <strong>Electronic and Postal Communications Act (EPOCA)</strong>, the <strong>Cybercrimes Act</strong>, and the <strong>Personal Data Protection Act</strong>.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  2. Company Information
                </h2>
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-100'}`}>
                  <p><strong>Atlas Softech Limited</strong></p>
                  <p>Dar Es Salaam, United Republic of Tanzania</p>
                  <p>Email: contact@atlassoftech.com</p>
                  <p>Phone: +255 799 227 202</p>
                </div>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  3. Description of Services
                </h2>
                <p>
                  Atlas Softech provides technology solutions and services including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Smart Agriculture Systems</li>
                  <li>Software Development and Application Services</li>
                  <li>Biometric Registration Systems</li>
                  <li>Health Application Solutions</li>
                  <li>Satellite Services</li>
                  <li>IT Infrastructure Setup and Management</li>
                  <li>Project Management Consultancy</li>
                  <li>Advanced Workspace Management Solutions</li>
                </ul>
                <p className="mt-4">
                  The specific terms, scope, and conditions of individual services shall be defined in separate service agreements or contracts.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  4. User Obligations and Conduct
                </h2>
                <p>
                  By using our services, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate, current, and complete information as required</li>
                  <li>Maintain the confidentiality of any account credentials</li>
                  <li>Use our services only for lawful purposes in compliance with Tanzanian law</li>
                  <li>Not engage in any activity that interferes with or disrupts our services</li>
                  <li>Not attempt to gain unauthorized access to our systems or networks</li>
                  <li>Not use our services to transmit malicious software or harmful content</li>
                  <li>Comply with all applicable laws and regulations of the United Republic of Tanzania</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  5. Intellectual Property Rights
                </h2>
                <p>
                  All content, materials, trademarks, logos, software, and intellectual property displayed or available through our services are the property of Atlas Softech Limited or our licensors and are protected under the intellectual property laws of Tanzania and international conventions.
                </p>
                <p>
                  You are granted a limited, non-exclusive, non-transferable license to access and use our services for their intended purpose. This license does not include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The right to copy, modify, or distribute our content without authorization</li>
                  <li>The right to reverse engineer or attempt to extract source code</li>
                  <li>The right to use our trademarks or branding without written consent</li>
                  <li>The right to sublicense or transfer any rights granted herein</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  6. Payment Terms
                </h2>
                <p>
                  Where applicable, the following payment terms shall apply:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>All fees and charges are quoted in Tanzanian Shillings (TZS) unless otherwise specified</li>
                  <li>Payment terms shall be as specified in individual service agreements</li>
                  <li>Late payments may be subject to interest at the rate prescribed by Tanzanian law</li>
                  <li>All applicable taxes, including Value Added Tax (VAT), shall be added as required by the Tanzania Revenue Authority</li>
                  <li>Invoices shall be issued in accordance with Tanzanian tax regulations</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  7. Confidentiality
                </h2>
                <p>
                  Both parties agree to maintain the confidentiality of any proprietary or confidential information disclosed during the course of our business relationship. This obligation shall survive the termination of these Terms.
                </p>
                <p>
                  Confidential information does not include information that:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Is or becomes publicly available through no fault of the receiving party</li>
                  <li>Was rightfully in the receiving party's possession prior to disclosure</li>
                  <li>Is independently developed without reference to confidential information</li>
                  <li>Is required to be disclosed by law or court order</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  8. Limitation of Liability
                </h2>
                <p>
                  To the maximum extent permitted by Tanzanian law:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Our total liability for any claims arising from or related to these Terms shall not exceed the total fees paid by you in the twelve (12) months preceding the claim</li>
                  <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>We shall not be liable for any loss of profits, revenue, data, or business opportunities</li>
                  <li>We shall not be liable for any delays or failures in performance caused by circumstances beyond our reasonable control (force majeure)</li>
                </ul>
                <p className="mt-4">
                  Nothing in these Terms shall exclude or limit liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded under Tanzanian law.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  9. Indemnification
                </h2>
                <p>
                  You agree to indemnify, defend, and hold harmless Atlas Softech Limited, its directors, officers, employees, and agents from and against any claims, damages, losses, liabilities, costs, and expenses (including legal fees) arising from:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your use of our services</li>
                  <li>Your breach of these Terms</li>
                  <li>Your violation of any applicable Tanzanian law or regulation</li>
                  <li>Your infringement of any third-party rights</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  10. Termination
                </h2>
                <p>
                  We may suspend or terminate your access to our services:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>If you breach any provision of these Terms</li>
                  <li>If required by law or regulatory authority</li>
                  <li>If we discontinue the services (with reasonable notice)</li>
                  <li>For any other legitimate business reason</li>
                </ul>
                <p className="mt-4">
                  Upon termination, your right to use our services will immediately cease. Provisions of these Terms that by their nature should survive termination shall continue in effect.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  11. Dispute Resolution
                </h2>
                <p>
                  Any dispute arising out of or in connection with these Terms shall be resolved as follows:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Negotiation:</strong> The parties shall first attempt to resolve the dispute through good faith negotiations</li>
                  <li><strong>Mediation:</strong> If negotiation fails, the parties may agree to submit the dispute to mediation</li>
                  <li><strong>Arbitration:</strong> If mediation fails, the dispute shall be referred to arbitration in accordance with the Arbitration Act of Tanzania</li>
                  <li><strong>Jurisdiction:</strong> The courts of the United Republic of Tanzania shall have exclusive jurisdiction over any disputes not resolved through arbitration</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  12. Governing Law
                </h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the United Republic of Tanzania, without regard to its conflict of law provisions. Any legal proceedings shall be conducted in the English or Swahili language as appropriate.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  13. Amendments
                </h2>
                <p>
                  We reserve the right to modify these Terms at any time. Material changes will be notified through our website or by other appropriate means. Your continued use of our services after such modifications constitutes acceptance of the updated Terms.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  14. Severability
                </h2>
                <p>
                  If any provision of these Terms is found to be invalid, illegal, or unenforceable under Tanzanian law, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  15. Entire Agreement
                </h2>
                <p>
                  These Terms, together with our Privacy Policy and any service-specific agreements, constitute the entire agreement between you and Atlas Softech Limited regarding the use of our services and supersede all prior agreements and understandings.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  16. Contact Information
                </h2>
                <p>
                  For any questions or concerns regarding these Terms, please contact us at:
                </p>
                <div className={`p-4 rounded-lg mt-4 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-100'}`}>
                  <p><strong>Atlas Softech Limited</strong></p>
                  <p>Legal Department</p>
                  <p>Dar Es Salaam, United Republic of Tanzania</p>
                  <p>Email: contact@atlassoftech.com</p>
                  <p>Phone: +255 799 227 202</p>
                </div>
              </section>

            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
